import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api-service';
import { HttpClient } from '@angular/common/http';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { AuthService } from './auth.service';
import { shareReplay } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { MainFilterItem } from 'app/components/new-navigation/rankings/models/mainFilterItem';
import { CapitalizePipe } from 'app/pipes/capitalize.pipe';
import { ReportConfig } from 'app/components/new-navigation/rankings/REPORT_CONFIG';

@Injectable({
  providedIn: 'root'
})
export class SdkIndexService extends BaseApiService {
  dataCache: any;
  capitalizePipe: CapitalizePipe = new CapitalizePipe();

  constructor(
    private _http: HttpClient,
    private _auth: AuthService,
    private titleService: Title,
    private metaService: Meta
  ) {
    super();
    this.dataCache = {};
  }

  public setPageDocumentMeta(reportConfig: ReportConfig): void {
    if (reportConfig) {
      this.titleService.setTitle(reportConfig.pageTitle);
      const tag: MetaDefinition = {
        name: 'description',
        content: reportConfig.pageMeta
      };
      this.metaService.updateTag(tag);
    }
  }

  public getRankingsData(currentConfig, group, start, limit) {
    // this.setPageDocumentMeta(this.currentConfig);

    const addParams = new Map();
    const reportId = currentConfig.reportId;
    const mainFilterItems = currentConfig.mainFilterItems;

    // Set initial state of the addParams
    mainFilterItems.forEach(mainFilterItem => {
      if (mainFilterItem.selected) {
        let value =
          mainFilterItem?.selected?.value || mainFilterItem.options[0]?.value;
        if (value) {
          const keys = mainFilterItem.facetKeys || [
            mainFilterItem.apiKey || mainFilterItem.key
          ];
          keys.forEach(key => {
            addParams.set(key, encodeURIComponent(value));
          });
        }
      }
    });

    addParams.set('group', group);

    // For Grid Data
    const sortColumns = currentConfig.gridHeaders
      .filter(header => header?.sort?.length > 0 && header.sortColumnKey)
      .map(
        header =>
          `${header.sortColumnKey} ${
            header.sort === 'sort-asc' ? 'asc' : 'desc'
          } ${
            header?.addSortColumnKeys
              ? ',' + header?.addSortColumnKeys.join(',')
              : ''
          }`
      );
    if (sortColumns.length > 0) {
      addParams.set('sortBy', sortColumns[0]);
    }

    const additionalQueryString = this.mapToUrlParam(addParams);

    const urlString =
      `${this.getRatingsBaseUrl('2020')}/getRankings?reportId=${reportId}` +
      `&${additionalQueryString}&start=${start}&limit=${limit}`;

    return this.getCachedObservable(urlString);
  }

  public getSdkInsightData(
    sdkId: string,
    appStore: string,
    selectedDate: string
  ) {
    let query = `WHERE id = '${sdkId}' AND appStore='${appStore}'`;
    if (selectedDate) {
      query += ` AND month='${selectedDate.slice(0, 6)}'`;
    }

    const urlString = `${this.getReportBaseUrl(
      '2018'
    )}/getWidget?q=${query}&reportId=mobileAppSdk&widgetId=mobileAppSdkSummary`;

    return this.getCachedObservable(urlString);
  }

  public loadMonthOptions(
    currentConfig: ReportConfig,
    mainFilterItems: Array<MainFilterItem>,
    monthFilterItem: MainFilterItem,
    selectedGroup: string
  ) {
    return new Promise(resolve => {
      const facets = [];
      facets.push('facet=date');
      let additionalQueryParams = '';
      const queryParams = [];
      if (monthFilterItem && monthFilterItem.dependencies) {
        const foundDependencyList = monthFilterItem.dependencies;
        if (foundDependencyList && foundDependencyList.length > 0) {
          foundDependencyList.forEach(foundDependency => {
            const foundDependencyFilter = mainFilterItems.filter(
              filter => foundDependency.key === filter.key
            );
            if (foundDependencyFilter && foundDependencyFilter[0]) {
              const value = foundDependencyFilter[0].selected.value;
              queryParams.push(`${foundDependency.key}=${value}`);
            }
          });
        }
      }
      if (queryParams.length > 0) {
        additionalQueryParams = queryParams.join('&');
      }
      const reportId = currentConfig.reportId;
      void this.getRankingsFacets(
        facets,
        reportId,
        selectedGroup,
        additionalQueryParams
      ).then(response => {
        const items = response['facets'][monthFilterItem.key];
        if (items && items.length > 0) {
          items.forEach(item => {
            const keys = Object.keys(item);
            const option: any = {};
            option.label = this.getQuarterOptionLabel(keys[0]);
            option.value = keys[0];

            monthFilterItem.options.push(option);
          });
          monthFilterItem.options = monthFilterItem.options.sort(
            (option1, option2) => option2.value.localeCompare(option1.value)
          );
          monthFilterItem.selected = monthFilterItem.options[0];

          this.setFilterItemSelected(monthFilterItem);

          // Load Facets based on dependency key and group
          const promises = [];
          mainFilterItems.forEach(mainFilterItem => {
            if (
              mainFilterItem &&
              mainFilterItem.dependencies &&
              mainFilterItem.dependencies.length > 0
            ) {
              const foundDependency = mainFilterItem.dependencies.some(
                dependency => dependency.key === monthFilterItem.key
              );
              if (foundDependency) {
                promises.push(
                  this.loadOptions(mainFilterItem, currentConfig, selectedGroup)
                );
              }
            }
          });
          if (promises.length > 0) {
            void Promise.all(promises).then(() => {
              resolve(true);
            });
          } else {
            resolve(true);
          }
        }
      });
    });
  }

  public loadOptions(
    mainFilterItem: MainFilterItem,
    currentConfig: ReportConfig,
    selectedGroup: string,
    route?: ActivatedRoute
  ) {
    return new Promise(resolve => {
      const facets = [];
      if (mainFilterItem.facetKeys) {
        mainFilterItem.facetKeys.forEach(key => {
          facets.push(`facet=${key}`);
        });
      } else {
        facets.push(`facet=${mainFilterItem?.key}`);
      }

      // Get the parameters based on dependency key and group
      let additionalParams = '';
      const addParams = new Map();
      const mainFilterItems = currentConfig.mainFilterItems;
      if (
        mainFilterItem.dependencies &&
        mainFilterItem.dependencies.length > 0
      ) {
        mainFilterItem.dependencies.forEach(dependency => {
          mainFilterItems
            .filter(
              item =>
                ((dependency.key && item.key === dependency.key) ||
                  (dependency.label && item.label === dependency.label)) &&
                item.selected &&
                item.selected.value !== ''
            )
            .forEach(item => {
              let value = item.selected.value;
              if (route && route.snapshot.params[item.routeParam]) {
                value = route.snapshot.params[item.routeParam];
              }
              addParams.set(item?.apiKey || item?.key, value);
            });
        });
      }

      if (addParams.size > 0) {
        additionalParams = this.mapToUrlParam(addParams);
      }

      void this.getRankingsFacets(
        facets,
        currentConfig.reportId,
        selectedGroup,
        additionalParams
      ).then(response => {
        let totalCount = 0;
        const newOptions = [];

        if (response && response['facets']) {
          const facetKeys = mainFilterItem.facetKeys || [
            mainFilterItem?.apiKey || mainFilterItem?.key
          ];

          if (mainFilterItem.key == 'sdkRisk') {
            const riskMap = {};
            const facetOptions = response['facets'] || [];
            facetKeys.forEach(facetKey => {
              facetOptions[facetKey].forEach(facetOption => {
                for (const [key, value] of Object.entries(facetOption)) {
                  let riskValue = riskMap[key] || 0;
                  riskMap[key] = riskValue + value;
                  totalCount += Number(value);
                }
              });
            });

            mainFilterItem.options.forEach(option => {
              option.count = riskMap[option.value];
            });
            mainFilterItem.options[0].count = totalCount;
          } else {
            facetKeys.forEach(facetKey => {
              const facetOptions = response['facets'][facetKey] || [];
              facetOptions.forEach(facetOption => {
                const keys = Object.keys(facetOption);
                const optionKey = keys[0];
                const count = facetOption[optionKey] || 0;
                const option: any = {
                  value: optionKey,
                  label: facetOption.name || optionKey,
                  name: facetOption.name || optionKey,
                  count: count
                };
                if (mainFilterItem.key == 'date') {
                  option.label = this.getQuarterOptionLabel(keys[0]);
                }
                totalCount += count;
                newOptions.push(option);
              });
            });
          }
        }

        // Add global values, and Apply Sorting
        if (mainFilterItem.key === 'date') {
          mainFilterItem.options = newOptions.sort((option1, option2) =>
            option2.value.localeCompare(option1.value)
          );
        } else if (mainFilterItem.key == 'type') {
          mainFilterItem.options = [
            {
              ...mainFilterItem.options[0],
              count: totalCount
            },
            ...newOptions
          ];
        }

        this.setFilterItemSelected(mainFilterItem);
        resolve(true);
      });
    });
  }

  private getRankingsFacets(
    facets: Array<string>,
    reportId: string,
    group: string,
    additionalQueryParams?: string
  ) {
    return new Promise(resolve => {
      const facetsString = facets.join('&');
      let url = `${this.getRatingsBaseUrl(
        '2020'
      )}/getRankingsFacets?reportId=${reportId}&group=${group}&${facetsString}`;

      if (additionalQueryParams && additionalQueryParams.length > 0) {
        url += `&${additionalQueryParams}`;
      }

      if (this.dataCache[url]) {
        setTimeout(() => {
          const cacheData = this.dataCache[url];
          resolve(JSON.parse(JSON.stringify(cacheData)));
        }, 300);
      } else {
        this._http.get(url).subscribe(res => {
          this.storeCache(res, this.dataCache, url);
          resolve(res);
        });
      }
    });
  }

  private getCachedObservable(urlString: string) {
    const cacheKey = `${this._auth.getAuthState()}:${urlString}`;
    let cachedObservable = this.dataCache[cacheKey];

    if (!cachedObservable) {
      cachedObservable = this._http.get(urlString).pipe(shareReplay());
      this.dataCache[cacheKey] = cachedObservable;
    }

    return cachedObservable.toPromise();
  }
}
